/**
 * Presentational integer from/to range with Done. Local draft until Done commits via onChange.
 * Mirrors date-range content layout for nested filter option use.
 */
import { useEffect, useState } from "react";
import { Input } from "../../../../../../../components/inputs/input/input";
import { useRmaListFilterPopoverClose } from "../../rma-list-filter-popover-close-context";
import RmaListFilterRangeRow from "../range-row/rma-list-filter-range-row";
import "./rma-list-filter-number-range-content.scss";

export interface IRmaListFilterNumberRangeValue {
    from?: number;
    to?: number;
}

export interface IRmaListFilterNumberRangeContentProps {
    value: IRmaListFilterNumberRangeValue;
    onChange: (value: { from: number; to: number }) => void;
}

const toInputString = (value: number | undefined): string => {
    return value === undefined || Number.isNaN(value) ? "" : String(value);
};

const parseNonNegativeInt = (raw: string): number | undefined => {
    if (raw.trim() === "") {
        return undefined;
    }
    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed < 0) {
        return undefined;
    }
    return parsed;
};

const RmaListFilterNumberRangeContent: React.FC<IRmaListFilterNumberRangeContentProps> = ({
    value,
    onChange,
}) => {
    const onRequestClose = useRmaListFilterPopoverClose();
    const [fromInput, setFromInput] = useState("");
    const [toInput, setToInput] = useState("");

    useEffect(() => {
        setFromInput(toInputString(value.from));
        setToInput(toInputString(value.to));
    }, [value.from, value.to]);

    const from = parseNonNegativeInt(fromInput);
    const to = parseNonNegativeInt(toInput);
    const fromFilled = fromInput.trim().length > 0;
    const toFilled = toInput.trim().length > 0;
    const fromInvalid = fromFilled && from === undefined;
    const toInvalid = toFilled && to === undefined;
    const bothValid = from !== undefined && to !== undefined;
    const rangeOrderInvalid = bothValid && from >= to;
    const canSubmit = bothValid && !rangeOrderInvalid;

    const fromHasError = fromInvalid || rangeOrderInvalid;
    const toHasError = toInvalid || rangeOrderInvalid;

    const onDone = () => {
        if (from === undefined || to === undefined || rangeOrderInvalid) {
            return;
        }
        onChange({ from, to });
        onRequestClose();
    };

    return (
        <div className="rma-list-filter-number-range-content">
            <RmaListFilterRangeRow
                fromInput={
                    <Input
                        type="number"
                        min={0}
                        temp
                        value={fromInput === "" ? null : from}
                        onChange={(next) => setFromInput(next === null || next === undefined ? "" : String(next))}
                        validationCallback={() => ({ isValid: !fromHasError, messages: [] })}
                    />
                }
                toInput={
                    <Input
                        type="number"
                        min={0}
                        temp
                        value={toInput === "" ? null : to}
                        onChange={(next) => setToInput(next === null || next === undefined ? "" : String(next))}
                        validationCallback={() => ({ isValid: !toHasError, messages: [] })}
                    />
                }
                canSubmit={canSubmit}
                onDone={onDone}
            />
        </div>
    );
};

export default RmaListFilterNumberRangeContent;
